import { Card, Pill, Text, Title } from "@appquality/appquality-design-system";
import { forwardRef } from "react";
import { PersonCircle } from "react-bootstrap-icons";
import styled from "styled-components";
import {
  getSeverityColor,
  getSeverityIcon,
  getStatusColor,
  PillType,
} from "./utils";

const CardWrapper = styled.div<{
  $isSelected: boolean;
  $pillType: PillType;
}>`
  cursor: pointer;
  margin-bottom: 0.75rem;
  border-left: ${(props) => (props.$isSelected ? "6px" : "4px")} solid
    ${(props) => props.theme.palette[props.$pillType]};

  &:hover > * {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
`;

const StyledCard = styled(Card)<{ $isSelected: boolean }>`
  background-color: ${(props) =>
    props.$isSelected
      ? props.theme.colors.gray200
      : props.theme.colors.white} !important;
  transition: box-shadow 0.2s ease, background-color 0.2s ease;
`;

const BugCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`;

const BugId = styled.span`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.gray600};
  font-weight: 400;
`;

const BugCardTitle = styled(Text)`
  font-weight: 600;
  margin: 0.25rem 0 0.5rem;
  color: ${({ theme }) => theme.palette.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.4;
`;

const BugCardFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  justify-content: space-between;
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.gray600};
`;

const TesterInfo = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-left: auto;
  text-align: right;
`;

const TesterName = styled.span`
  font-weight: 500;
`;

interface BugCardProps {
  bug: {
    id: number;
    title: string;
    tester: {
      id: number;
      name: string;
      surname: string;
    };
    severity: {
      id: number;
      name: string;
    };
    status: {
      id: number;
      name: string;
    };
    tags?: {
      id: number;
      name: string;
    }[];
  };
  isSelected: boolean;
  onClick: () => void;
}

const BugCard = forwardRef<HTMLDivElement, BugCardProps>(
  ({ bug, isSelected, onClick }, ref) => {
    const severityType = getSeverityColor(bug.severity.name);
    const statusType = getStatusColor(bug.status.name);
    const SeverityIcon = getSeverityIcon(bug.severity.name);

    return (
      <CardWrapper
        ref={ref}
        $isSelected={isSelected}
        $pillType={statusType}
        onClick={onClick}
      >
        <StyledCard $isSelected={isSelected} bodyClass="aq-p-3">
          <BugCardHeader>
            <BugId>BUG ID: #{bug.id}</BugId>
            <Pill type={statusType}>{bug.status.name}</Pill>
          </BugCardHeader>

          <BugCardTitle>{bug.title}</BugCardTitle>

          <BugCardFooter>
            <div
              style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}
            >
              <Title size="xs" color={severityType} className="aq-m-0">
                {bug.severity.name}
              </Title>
              <SeverityIcon size={11} color={severityType} />
            </div>
            <TesterInfo>
              <PersonCircle size={14} />
              <TesterName>
                {bug.tester.name} {bug.tester.surname.charAt(0)}. (T
                {bug.tester.id})
              </TesterName>
            </TesterInfo>
          </BugCardFooter>
        </StyledCard>
      </CardWrapper>
    );
  },
);

BugCard.displayName = "BugCard";

export default BugCard;
